import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { Moveable, PinButton } from "client/Components";
import {
	AvailableMenuItem,
	MenuItemKind
} from "./GlobalNavigationSidebar";
import classNames from "classnames";
import {
	clamp,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { SubNavigationLoadingIndicator } from "./SubNavigationLoadingIndicator";
import { SubNavigationHeader } from "./SubNavigationHeader";
import styles from "./SubNavigation.module.less";
import { useActivePanelOverrideStore } from "./useActivePanelOverrideStore";
import { SubNavigationBody } from "./SubNavigationBody";
import { useResizeObserver } from "client/Hooks";
import { Coordinate } from "client/Misc/commonTypes";

export type SubNavigationProps = {
	activePanel: MenuItemKind | undefined;
	isPinned: boolean;
	hoveredItem: MenuItemKind | undefined;
	availableMenuItems: readonly AvailableMenuItem[];
	onPinnedChange: (isPinned: boolean) => void;
	onUnhover(): void;
	onResizeActivePanel?(newWidth: number | null): void;
	isResponsive: boolean;
};

export const SubNavigation = (props: SubNavigationProps): ReactElement => {
	const {
		hoveredItem,
		activePanel,
		isPinned,
		onPinnedChange,
		availableMenuItems,
		onUnhover,
		onResizeActivePanel,
		isResponsive
	} = props;

	const {
		setSubNavigationHost,
		hasActiveSidebarOverride
	} = useActivePanelOverrideStore(
		x => ({
			setSubNavigationHost: x.setHost,
			hasActiveSidebarOverride: x.hasOverride
		}),
		shallowComparison
	);

	return (
		<div
			{...setTrackingId("sub-navigation")}
			className={styles.root}
		>
			{availableMenuItems.map(x => (
				<SubNavigationSection
					menuItem={x}
					activePanel={activePanel}
					hoveredItem={hoveredItem}
					hasActivePanelOverride={hasActiveSidebarOverride}
					isPinned={isPinned}
					onIsPinnedChange={onPinnedChange}
					key={x.kind}
					panelContentRef={
						activePanel === x.kind
							? setSubNavigationHost
							: undefined
					}
					onUnhover={onUnhover}
					trackingId={x.trackingId}
					onResize={
						isPinned && activePanel === x.kind && !isResponsive
							? onResizeActivePanel
							: undefined
					}
				/>
			))}
		</div>
	);
};

SubNavigation.displayName="SubNavigation";

SubNavigation.Header = SubNavigationHeader;
SubNavigation.Body = SubNavigationBody;
SubNavigation.LoadingIndicator = SubNavigationLoadingIndicator;

export type GenericNavigationPanelProps = {
	isVisible: boolean;
	isPinned: boolean;
	isActive: boolean;
};

type SubNavigationSectionProps = {
	menuItem: AvailableMenuItem;
	isPinned: boolean;
	onIsPinnedChange(isPinned: boolean): void;
	onUnhover(): void;
	activePanel: MenuItemKind | undefined;
	hoveredItem: MenuItemKind | undefined;
	hasActivePanelOverride: boolean;
	panelContentRef?: Ref<HTMLDivElement>;
	trackingId: string;
	onResize?(newWidth: number | null): void;
};

const SubNavigationSection = (props: SubNavigationSectionProps): ReactElement | null => {
	const {
		menuItem,
		isPinned,
		onIsPinnedChange,
		activePanel,
		hoveredItem,
		onUnhover,
		hasActivePanelOverride,
		panelContentRef,
		trackingId,
		onResize
	} = props;

	const [widthBeforeResize, setWidthBeforeResize] = useState<number | null>(null);
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const PanelContent: React.FC<GenericNavigationPanelProps> | undefined = menuItem.panel;

	const isHoveredPanel: boolean = hoveredItem === menuItem.kind;
	const isActivePanel: boolean = activePanel === menuItem.kind;
	const isCurrentPanelPinned: boolean = isPinned && isActivePanel;
	const { wasInitialized, width: measuredWidth } = useResizeObserver(ref);

	useLayoutEffect(
		(): void => {
			hasImplicitDependencyOn(measuredWidth);
			if (!wasInitialized) {
				return;
			}

			// Note getBoundingClientRect is used here because visibility depends on the styles
			// So we need to wait for document to reflow to get the actual width of the element
			const actualRect: DOMRect | undefined = ref.current
				?.getBoundingClientRect();

			if (!actualRect) {
				return;
			}

			const actualWidth: number = actualRect.width;

			if (actualWidth === 0 && isHoveredPanel) {
				onUnhover();
			}
		},
		[
			isHoveredPanel,
			onUnhover,
			wasInitialized,
			measuredWidth,
			menuItem.kind
		]
	);

	const startResizingHandler = useCallback(
		(): void => {
			setWidthBeforeResize(measuredWidth);
		},
		[measuredWidth]
	);

	const resizeHandler = useMemo(
		(): ((offset: Coordinate) => void) | undefined => {
			if (!onResize) {
				return undefined;
			}

			return (offset: Coordinate): void => {
				if (isNil(widthBeforeResize)) {
					return;
				}

				onResize(
					clamp(
						widthBeforeResize + offset.x,
						250,
						500
					)
				);
			};
		},
		[onResize, widthBeforeResize]
	);

	const endResizingHandler = useCallback(
		(): void => {
			setWidthBeforeResize(null);
		},
		[]
	);

	if (isNil(PanelContent)) {
		return null;
	}

	return (
		<div
			{...setTrackingId(trackingId)}
			key={menuItem.kind}
			ref={ref}
			className={
				classNames(
					styles.panel,
					{
						[styles.panel_isVisible]: isHoveredPanel,
						[styles.panel_isPinned]: isCurrentPanelPinned,
						[styles.panel_active]: isActivePanel
					}
				)
			}
			aria-current={isActivePanel}
		>
			<div className={styles.container}>
				<div
					ref={panelContentRef}
					className={styles.content}
				>
					{(!isActivePanel || !hasActivePanelOverride) && (
						<PanelContent
							isPinned={isCurrentPanelPinned}
							isVisible={measuredWidth !== 0}
							isActive={isActivePanel}
						/>
					)}
				</div>
				{isNotNil(resizeHandler) && (
					<div className={styles.resizeHandle}>
						<Moveable
							onStart={startResizingHandler}
							onMove={resizeHandler}
							onEnd={endResizingHandler}
						/>
					</div>
				)}
				{isActivePanel && (
					<div className={styles.pinButtonContainer}>
						<PinButton
							onClick={() => onIsPinnedChange(!isPinned)}
							isPinned={isPinned}
							trackingId="pin-unpin"
						/>
					</div>
				)}
			</div>
		</div>
	);
};
SubNavigationSection.displayName = "SubNavigationSection";